import { useEffect, useRef, useState, type CSSProperties, type DragEvent, type FocusEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { withConflictConfirm } from '../availability/conflicts';
import { SupplierLacksItem } from '../subrentals/SupplierLacksItem';
import { priceLine } from '@app/shared/price-calc';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { daysToMilli, milliToDays } from '../pricing/units';
import { CrewPeopleInfo } from './CrewPeopleInfo';
import { CostCell } from './CostCell';
import { ShortageHolders } from './ShortageHolders';
import { SupplierCell } from './SupplierCell';
import { transportNote } from './transport';
import { RowMenu } from '../ui/RowMenu';
import { bpToPct, pctToBp, type QuoteLine } from './types';
import { NumberInput } from '../ui/NumberInput';
import { formatDecimal, parseScaled } from '../ui/numbers';
import { useRowGuard } from '../ui/unsaved';
import { rentalsDetail } from '../subrentals/rented';
import { markStyle, useContractorColors } from '../contractors/colors';
import { catalogTitle, useCatalogGesture } from '../nav/catalogLink';
import { ExtraDaysDialog, ExtraDaysNote, type ExtraDays } from '../availability/extraDays';

/**
 * Which amount columns the role may see (rule 7a: hidden fields are neither shown nor sent). `discountHidden` (P8,
 * point 9): the role sees discounts, but no line has one yet, so the column "Rabat %" is not shown (still counted).
 */
export type Columns = {
  price: boolean;
  discount: boolean;
  net: boolean;
  discountHidden?: boolean;
  /** F29: "Koszt" (owner / finance) */
  cost?: boolean;
  /** the cost may be typed (any version) */
  costEditable?: boolean;
  /** Owner 2026-10-21: "Dostawca" — once the version has a line from a supplier (seen with the costs only) */
  supplier?: boolean;
  /** the supplier may be changed (moves the rental) */
  supplierEditable?: boolean;
};

type Draft = { name: string; price: string; quantity: string; days: string; discount: string; description: string };

const draftOf = (l: QuoteLine): Draft => ({
  name: l.name,
  price: l.unitPriceGrosze !== undefined ? plnToInput(l.unitPriceGrosze) : '',
  quantity: formatDecimal(l.quantity),
  days: milliToDays(l.daysMilli),
  discount: l.discountBp !== undefined ? bpToPct(l.discountBp) : '',
  description: l.description,
});

type Parsed = { name: string; unitPriceGrosze?: number; quantity: number; daysMilli: number; discountBp?: number; description: string };

/** The draft as numbers, or the Polish message of the first field that is not valid. */
function parse(d: Draft, cols: Columns): { ok: true; value: Parsed } | { ok: false; field: keyof Draft; message: string } {
  if (d.name.trim() === '') return { ok: false, field: 'name', message: 'Podaj nazwę pozycji' };
  // thousandths, like the days (F24b: "0,5" of a set)
  const quantityMilli = parseScaled(d.quantity, 3, { maxIntegerDigits: 7 });
  if (quantityMilli === null) return { ok: false, field: 'quantity', message: 'Ilość od 0, np. 2 albo 0,5 (0 = w opcji)' };
  const daysMilli = daysToMilli(d.days);
  if (daysMilli === null) return { ok: false, field: 'days', message: 'Liczba dni od 0,5, np. 1 albo 1,5' };
  const value: Parsed = { name: d.name.trim(), quantity: quantityMilli / 1000, daysMilli, description: d.description.trim() };
  if (cols.price) {
    try {
      value.unitPriceGrosze = parsePln(d.price === '' ? '0' : d.price);
    } catch (e) {
      return { ok: false, field: 'price', message: (e as Error).message };
    }
  }
  if (cols.discount) {
    const bp = pctToBp(d.discount);
    if (bp === null) return { ok: false, field: 'discount', message: 'Rabat od 0 do 100%, np. 10' };
    value.discountBp = bp;
  }
  return { ok: true, value };
}

type Props = {
  line: QuoteLine;
  /** Shown in the first column of the section's first row (as in the owner's template); null on the other rows. */
  sectionCell: React.ReactNode;
  dayPctsBp: number[];
  cols: Columns;
  editable: boolean;
  onPreview: (lineId: string, net: number | null) => void;
  onSaved: (line: QuoteLine) => void;
  onDeleted: (lineId: string) => void;
  onConflict: () => void;
  /** P8 (point 27): an own line above the line, with the right to add to the catalog: "Dodaj do katalogu". */
  onAddToCatalog?: ((line: QuoteLine) => void) | undefined;
  /** Owner 2026-10-23 21:05: „Dopożycz” under a shortage (with the right to add rentals): the line and what it lacks. */
  onSubrent?: ((line: QuoteLine) => void) | undefined;
  /** Owner 19:48 („czy na pewno” before every delete): × asks through the table; without it × deletes at once. */
  onRemove?: ((line: QuoteLine) => void) | undefined;
  /** Owner 2026-10-24 11:03: a note under the line's name, in its row (the category note of "Dodaj…"). */
  notice?: ReactNode;
  /** Owner 2026-10-25: the event of the quote — the crew hover's link „Obsada”. */
  eventId?: string | undefined;
  /**
   * Owner 2026-10-23 18:34 (a draft on a desktop): a click on the row outside its fields and buttons selects it
   * (`aria-selected`, the focus on the row: the keys of QuoteTable act on it); the drag handle and its drop target.
   */
  select?: {
    selected: boolean;
    /** owner 19:46: the click's keys — Shift a range from the last clicked line, ⌘ (Windows: Ctrl) one line in or out */
    onSelect: (keys: { range: boolean; toggle: boolean }) => void;
    grip: ReactNode;
    drop?: 'before' | 'after' | null | undefined;
    dragging?: boolean | undefined;
    onDragOver: (e: DragEvent<HTMLTableRowElement>) => void;
    onDrop: (e: DragEvent<HTMLTableRowElement>) => void;
  } | undefined;
  /** Owner 2026-10-23 20:53: Ctrl+klik (Mac; Windows: Alt+klik) opens the line in the catalog; absent: nowhere to go. */
  openCatalog?: (() => void) | undefined;
  /**
   * Owner 2026-10-24 16:15: a plain click on the line's name (a draft) opens its change to another line („Zmień”); the
   * keys of the selection (Shift / ⌘ / Ctrl) and of the catalog still do what they did. Absent: the name is only text.
   */
  onReplaceStart?: (() => void) | undefined;
  /** The change is open: this field stands in place of the name (`ReplaceLine`). */
  replacing?: ReactNode;
};

/** The row's controls: a click there is theirs, it does not select the row. */
/** One supplier stripe (3 px) and its gap (2 px): the room a stripe takes in the section column (quotes.css). */
const STRIPE_STEP_PX = 5;
// .q-short-who (owner 2026-10-25 00:39): a click on „brakuje N” opens who holds the item, it does not pick the line
const CONTROLS = 'input, textarea, select, button, a, label, [role="menu"], [role="listbox"], [role="option"], .q-short-who';

/**
 * One line of the quote, edited in place like a spreadsheet: Tab goes from field to field, Enter (or leaving the
 * row) saves only what changed, Esc puts the row back. While typing, the amount is computed with the very
 * function the server uses (`priceLine`); after saving, the server's numbers are shown.
 */
export function QuoteLineRow({ line, sectionCell, dayPctsBp, cols, editable, onPreview, onSaved, onDeleted, onConflict, onAddToCatalog, onSubrent, onRemove, select, openCatalog, notice, onReplaceStart, replacing, eventId }: Props) {
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftOf(line));
  // owner 2026-10-23 20:50: a supplier's lines in its colour, not red (grey without one); screen only
  const colors = useContractorColors();
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingDays, setEditingDays] = useState(false);
  const row = useRef<HTMLTableRowElement | null>(null);
  /** A conflict's message must survive the fresh line that the refresh brings. */
  const keepError = useRef(false);

  // the server answered (or someone else changed the line): show its values
  useEffect(() => {
    setDraft(draftOf(line));
    if (keepError.current) keepError.current = false;
    else setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.version, line.id]);

  const parsed = parse(draft, cols);
  const original = draftOf(line);
  const dirty = (Object.keys(draft) as (keyof Draft)[]).some((k) => draft[k] !== original[k]);

  // live amount: the same calculation as the server, with the version's day scheme
  let preview: number | null = null;
  // without the discount (hidden) the preview could not be right: then only the saved amount is shown
  if (parsed.ok && cols.net && cols.price && cols.discount) {
    try {
      preview = priceLine(
        { unitPriceGrosze: parsed.value.unitPriceGrosze ?? 0, quantityMilli: Math.round(parsed.value.quantity * 1000), daysMilli: parsed.value.daysMilli, linear: line.linear, discountBp: parsed.value.discountBp ?? 0 },
        dayPctsBp,
      );
    } catch {
      preview = null;
    }
  }
  const shownNet = dirty && preview !== null ? preview : (line.netGrosze ?? null);
  useEffect(() => {
    onPreview(line.id, dirty ? preview : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, preview, line.id]);

  async function save(): Promise<boolean> {
    if (!dirty || saving) return !dirty;
    if (!parsed.ok) {
      setError(parsed.message);
      return false;
    }
    const v = parsed.value;
    const body: Record<string, unknown> = {};
    if (v.name !== line.name) body.name = v.name;
    if (v.quantity !== line.quantity) body.quantity = v.quantity;
    if (v.daysMilli !== line.daysMilli) body.daysMilli = v.daysMilli;
    if (cols.price && v.unitPriceGrosze !== line.unitPriceGrosze) body.unitPriceGrosze = v.unitPriceGrosze;
    if (cols.discount && v.discountBp !== line.discountBp) body.discountBp = v.discountBp;
    if (v.description !== line.description) body.description = v.description;
    if (Object.keys(body).length === 0) {
      setDraft(draftOf(line));
      return true;
    }
    setSaving(true);
    try {
      // more of gear another event reserved first: „Dodaj mimo to” / „Anuluj” (owner 2026-10-24); „Anuluj” puts it back
      const saved = await withConflictConfirm((confirmConflicts) => api<QuoteLine>(`/api/quotes/lines/${line.id}`, { method: 'PATCH', body: { ...body, version: line.version, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
      if (!saved) {
        setDraft(draftOf(line));
        return true;
      }
      onSaved(saved);
      setError('');
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        keepError.current = true;
        setError('Ktoś w tym czasie zmienił tę pozycję. Pokazuję aktualne wartości.');
        onConflict();
      } else setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zapisać');
      return false;
    } finally {
      setSaving(false);
    }
  }
  // owner 2026-10-23 21:14: typed and not saved (a wrong number; leaving the row saves): leaving the card, the tab or
  // the screen waits for that save or asks first
  const guard = useRowGuard(editable && dirty, save);

  /** Owner 2026-10-23: "Ryczałt / Wg km" of a transport line; null = by the threshold again. Saved at once. */
  async function setMethod(transportMode: 'flat' | 'distance' | null) {
    try {
      onSaved(await api<QuoteLine>(`/api/quotes/lines/${line.id}`, { method: 'PATCH', body: { transportMode, version: line.version } }));
      setError('');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        keepError.current = true;
        setError('Ktoś w tym czasie zmienił tę pozycję. Pokazuję aktualne wartości.');
        onConflict();
      } else setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zapisać');
    }
  }

  /**
   * „Dni rezerwacji…” (owner 2026-10-24 15:29): the event's days of the item, one setting with the packlist's; never the
   * price, never a write of the quote. False when nothing was saved („Anuluj” in the conflict question).
   */
  async function saveDays(extraDays: ExtraDays | null): Promise<boolean> {
    const saved = await withConflictConfirm((confirmConflicts) => api<QuoteLine>(`/api/quotes/lines/${line.id}/extra-days`, { method: 'PUT', body: { extraDays, version: line.extraDaysVersion ?? 0, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
    if (!saved) return false;
    onSaved(saved);
    // the days are the event's item's: its other lines here show them too (the table reads the version again)
    onConflict();
    return true;
  }
  const ownGear = line.type === 'item' && !!line.itemId && !line.supplierId;

  // no "are you sure" in the quote (owner): × removes the line at once
  async function remove() {
    try {
      await api(`/api/quotes/lines/${line.id}?version=${line.version}`, { method: 'DELETE' });
      onDeleted(line.id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) onConflict();
      setError(e instanceof ApiError ? e.message : 'Nie udało się usunąć');
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTableRowElement>) {
    // the name and the description are text areas (they wrap); Enter still saves there, no new line
    if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape' && dirty) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(draftOf(line));
      setError('');
    }
  }
  const catalog = useCatalogGesture(openCatalog);
  function onClick(e: MouseEvent<HTMLTableRowElement>) {
    if (catalog.onClick(e)) return;
    if (!select || (e.target instanceof Element && e.target.closest(CONTROLS))) return;
    // typed in a field, then the row clicked: the focus stays in the row (no blur save), so save it here
    if (dirty) void guard.save();
    const mac = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
    select.onSelect({ range: e.shiftKey, toggle: mac ? e.metaKey : e.ctrlKey });
    row.current?.focus({ preventScroll: true });
  }
  // leaving the row saves it (as in a spreadsheet); moving between its own fields does not
  function onBlur(e: FocusEvent<HTMLTableRowElement>) {
    if (e.relatedTarget instanceof Node && row.current?.contains(e.relatedTarget)) return;
    void guard.save();
  }

  const label = line.name;
  const input = (key: keyof Draft, aria: string, numeric = true) => (
    numeric ? (
      <NumberInput
        className="cell-input num"
        kind={key === 'price' ? 'money' : 'decimal'}
        aria-label={`${aria}: ${label}`}
        aria-invalid={!parsed.ok && parsed.field === key ? true : undefined}
        value={draft[key]}
        onValueChange={(v) => setDraft((d) => ({ ...d, [key]: v }))}
      />
    ) : (
      // a text area as wide as its column: a long name or description wraps instead of widening the table
      <textarea
        rows={1}
        className="cell-input"
        aria-label={`${aria}: ${label}`}
        aria-invalid={!parsed.ok && parsed.field === key ? true : undefined}
        spellCheck={false}
        value={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      />
    )
  );
  /** Read-only text with the inset of an input, so text, inputs and the header start at the same edge. */
  const text = (value: string) => <span className="q-text">{value}</span>;
  /**
   * The name that opens its change (owner 2026-10-24 16:15): a plain click or Enter / Space on it. A click with Shift,
   * ⌘, Ctrl or Alt goes on to the row (a range or one more line selected, the catalog opened — #116, #155).
   */
  const pickName = (value: string) => (
    <span
      className="q-text q-name-pick"
      role="button"
      tabIndex={0}
      aria-label={`Zmień pozycję: ${value}`}
      title={`Kliknij, aby zmienić na inną pozycję${openCatalog ? ` · ${catalogTitle()}` : ''}`}
      data-feature-id="quotes.replace_line"
      onClick={(e) => {
        if (e.button !== 0 || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
        e.stopPropagation();
        // typed in a field of the row: saved first, as when the row is clicked
        if (dirty) void guard.save();
        onReplaceStart!();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        onReplaceStart!();
      }}
    >
      {value}
    </span>
  );
  // equipment: "Marka" is its name; services and own lines read as the PDF does
  // owner 21:27: a position only the supplier has is stored as an own line with a supplier — it says so, not „pozycja własna”
  const kind = line.type === 'service' ? 'usługa' : line.type === 'custom' ? (line.supplierId ? 'tylko u dostawcy' : 'pozycja własna') : null;
  const transport = line.transport ? transportNote(line.transport) : null;
  // the method changes the price: only in a draft and for whoever sees the price; also without km (review M3: a method
  // chosen by hand can go back to "Wg progu")
  const mayChooseMethod = editable && cols.price && !!line.transport;
  // owner 21:05: rented (wholly or partly) — black as any own line, a thin stripe per supplier and the info on hover
  // pkt 6 (owner 2026-10-24 16:15): a role without costs gets the pieces per supplier only — the same hover, no amounts
  const rentals = line.rentals ?? line.rentalPieces ?? [];
  const suppliers = [...new Map(rentals.map((r) => [r.supplierId, r.supplierName])).entries()];
  // owner 2026-10-24 (pkt 7): only on hover — per supplier pieces × price = cost and the margin (the line's net per piece)
  const netPerPieceGrosze = line.netGrosze !== undefined && line.quantity > 0 ? line.netGrosze / line.quantity : undefined;
  const rentedInfo = rentals.length > 0 ? rentalsDetail(rentals, netPerPieceGrosze !== undefined ? { netPerPieceGrosze } : {}) : undefined;
  // pkt 4: the stripes grow leftwards into the section column, which keeps room for them
  const stripesStyle = suppliers.length > 0 ? ({ '--q-stripes-w': `${suppliers.length * STRIPE_STEP_PX}px` } as CSSProperties) : undefined;
  const rowStyle = line.supplierId || stripesStyle ? { ...(line.supplierId ? markStyle(colors.get(line.supplierId)) : {}), ...stripesStyle } : undefined;

  return (
    <>
      <tr
        ref={(el) => {
          row.current = el;
          guard.ref(el);
        }}
        className={`sheet-row${dirty ? ' dirty' : ''}${error ? ' has-error' : ''}${line.supplierId ? ' q-from-supplier' : ''}${line.rentalMark?.kind === 'anulowane' ? ' q-rental-cancelled' : ''}${select?.selected ? ' q-selected' : ''}${select?.drop ? ` q-drop-${select.drop}` : ''}${select?.dragging ? ' q-dragging' : ''}`}
        onKeyDown={onKeyDown}
        onBlur={editable ? onBlur : undefined}
        data-line-id={line.id}
        tabIndex={select ? -1 : undefined}
        aria-selected={select ? select.selected : undefined}
        onClick={select || openCatalog ? onClick : undefined}
        onContextMenu={openCatalog ? (e) => void catalog.onContextMenu(e) : undefined}
        // Shift+click picks a range of lines, not the text between them
        onMouseDown={
          select
            ? (e) => {
                if (e.shiftKey && !(e.target instanceof Element && e.target.closest(CONTROLS))) e.preventDefault();
              }
            : undefined
        }
        onDragOver={select?.onDragOver}
        onDrop={select?.onDrop}
        title={rentedInfo}
        style={rowStyle}
      >
        <th scope="row" className="sheet-section-cell">
          {sectionCell}
        </th>
        <td
          className={`q-name sheet-main${suppliers.length > 0 ? ' q-rented' : ''}`}
          title={openCatalog ? catalogTitle() : undefined}
          // owner 2026-10-25: pointing at a crew service shows who is behind it
          onMouseEnter={line.crewPeople ? () => setHovered(true) : undefined}
          onMouseLeave={line.crewPeople ? () => setHovered(false) : undefined}
        >
          {suppliers.length > 0 && (
            <span className="q-rent-stripes" aria-hidden="true" data-testid="rent-stripes">
              {suppliers.map(([id, name]) => (
                // a supplier off our list (owner 2026-10-24 16:15) has no colour of its own
                <i key={id ?? `off:${name}`} data-supplier-id={id ?? undefined} style={markStyle(id ? colors.get(id) : undefined)} />
              ))}
            </span>
          )}
          {/* owner 2026-10-23 10:34: a small ⚙ at the left of the name instead of a big "Ryczałt / Wg km" select;
              15:21: half size, in the gutter left of the name, shown on hover / focus (quotes.css .q-line-gear) */}
          {mayChooseMethod && (
            <span className="q-line-gear">
              <RowMenu
                icon="gear"
                floating
                label={`Sposób liczenia transportu: ${label}`}
                actions={(
                  [
                    ['flat', 'Ryczałt'],
                    ['distance', 'Wg km'],
                    [null, 'Wg progu (domyślnie)'],
                  ] as const
                ).map(([mode, name]) => ({
                  label: name,
                  checked: (line.transport!.chosenMode ?? null) === mode,
                  title: line.priceOverridden ? 'Zmiana sposobu zastąpi wpisaną cenę wyliczoną' : mode === null ? `Wg progu z „Ustawień transportu”: do ${line.transport!.localKm} km ryczałt, dalej wg km` : undefined,
                  onSelect: () => void setMethod(mode),
                }))}
              />
            </span>
          )}
          {replacing ?? (editable && line.type === 'custom' ? input('name', 'Nazwa', false) : onReplaceStart ? pickName(line.name) : text(line.name))}
          {kind && (
            <span className="q-kind">
              {kind}
              {/* an own line's name is typed in place: its change to another line is offered beside it (16:15) */}
              {onReplaceStart && line.type === 'custom' && !replacing && (
                <span className="q-replace-own">
                  {' · '}
                  <button type="button" aria-label={`Zmień na inną pozycję: ${label}`} title="Zmień na pozycję z katalogu, cennika albo od dostawcy" onClick={onReplaceStart} data-feature-id="quotes.replace_line">
                    zmień
                  </button>
                </span>
              )}
              {/* P8: shows when the row is pointed at or focused */}
              {onAddToCatalog && (
                <span className="q-to-catalog">
                  {' · '}
                  <button type="button" aria-label={`Dodaj do katalogu: ${label}`} title="Dodaj tę pozycję do katalogu sprzętu" onClick={() => onAddToCatalog(line)} data-feature-id="quotes.add_to_catalog">
                    dodaj do katalogu
                  </button>
                </span>
              )}
            </span>
          )}
          {/* owner 2026-10-24 15:35: „Info o zajętości dłuższej na czerwono wyraźnie” — for everyone who sees the line */}
          <ExtraDaysNote held={line.extraHeld} />
          {line.noCatalogPrice && <span className="q-kind warn">brak ceny w katalogu</span>}
          {/* owner 2026-11-04: the packlist's „Pozycja własna” added without a price („potrzebna wycena” accent) */}
          {line.needsPrice && (
            <span className="q-kind" data-feature-id="packlists.custom">
              <span className="quote-flag">do uzupełnienia</span>
            </span>
          )}
          {line.lockedRentals?.map((h) => (
            <span key={h.supplierName} className="q-kind warn" data-feature-id="quotes.subrent">
              {h.reason === 'picked' ? `Dopożyczenie u ${h.supplierName} jest już odebrane — zmień je ręcznie w Dopożyczeniach` : `Dopożyczenie u ${h.supplierName} ma koszt wpisany ręcznie — zmniejsz je w Dopożyczeniach`}
            </span>
          ))}
          {line.shortage && (
            // owner 21:0x: „w sprzętówce jest dobrze i chcę tak samo” — the packlist's note under the line
            <span className="q-short" data-feature-id="quotes.subrent">
              {/* owner 2026-10-25 00:39: on hover / focus — who holds the item, each event a link to its packlist */}
              <ShortageHolders holders={line.shortage.holders}>brakuje {line.shortage.missing}</ShortageHolders>
              {onSubrent && (
                <button className="btn small ghost q-subrent" type="button" aria-label={`Dopożycz: ${label}`} title="Dopożycz brakującą ilość od dostawcy (zakładka „Dopożyczenia” wydarzenia)" onClick={() => onSubrent(line)}>
                  Dopożycz
                </button>
              )}
            </span>
          )}
          {line.supplierId && (
            <span className="q-kind q-kind-supplier" title="Pozycja z katalogu dostawcy: dopożyczenie w zakładce „Dopożyczenia”. Kolor dostawcy widać tylko na ekranie, nie w PDF.">
              od dostawcy
            </span>
          )}
          {line.noSupplierPrice && (
            // owner 2026-10-23 20:59: a line saved at a supplier without the position — said so, with the way to add it
            <span className="q-kind warn" title="Dostawca nie ma tej pozycji w swoim katalogu: dopożyczenie ma koszt wpisany ręcznie. Dodaj pozycję w katalogu dostawcy albo wpisz koszt w kolumnie „Koszt”.">
              <SupplierLacksItem marker supplierId={line.supplierId} supplierName={line.supplierName} itemId={line.itemId} />
            </span>
          )}
          {(line.rentalShort ?? 0) > 0 && (
            // pkt 3 (review): the supplier has fewer than the line wants — the rental holds what it has, the rest is short
            <span className="q-short" data-feature-id="quotes.suppliers" title="Dostawca nie ma więcej sztuk tej pozycji w katalogu („Ilość posiadanych”). Zmniejsz ilość, zmień dostawcę albo dopożycz resztę u innego.">
              brakuje {line.rentalShort} — {line.supplierName ?? 'dostawca'} nie ma więcej
            </span>
          )}
          {line.rentalMark && (
            // owner 2026-11-01: the rental of a closed event — cancelled with it (the line costs nothing) or to settle
            <span className={`q-kind rental-mark rental-mark-${line.rentalMark.kind}`}>{line.rentalMark.label}</span>
          )}
          {line.rentalMismatch && (
            <span className="q-kind warn" title="Sprzęt już odebrany: kosztorys nie zmienia dopożyczenia. Popraw dopożyczenie w „Dopożyczeniach” albo pozycję tutaj.">
              inna ilość lub dostawca niż w dopożyczeniu
            </span>
          )}
          {line.supplierId && line.rentalId === null && (
            <span className="q-kind" title="Dopożyczenia idą za wersją, która obowiązuje: zaakceptowaną, a bez akceptacji — najnowszą. Ta wersja nie obowiązuje albo dopożyczenie usunięto w „Dopożyczeniach”.">
              bez dopożyczenia (wersja nie obowiązuje)
            </span>
          )}
          {transport && (
            <span className={`q-kind q-transport${line.transport!.km === null ? ' warn' : ''}`} data-feature-id="pricing.transport" title={transport.detail || undefined}>
              {transport.km && <strong>{transport.km}</strong>}
              {transport.km && ' · '}
              {transport.how}
              {transport.chosen && (
                <>
                  {' · '}
                  <span title="Sposób liczenia wybrany ręcznie: zostaje, gdy zmieni się odległość wydarzenia">wybrane ręcznie</span>
                </>
              )}
            </span>
          )}
          {line.origin === 'rule' && (
            <span className="q-kind" title="Pozycję dodała reguła (ekran „Reguły”) — np. z zakresu prac wydarzenia" data-feature-id="quotes.rule_services">
              z reguły
            </span>
          )}
          {line.origin !== 'crew' && line.ruleQuantity != null && line.ruleQuantity !== line.quantity && (
            <span className="q-kind warn" title="Ilość zmieniona ręcznie: reguła jej nie nadpisze. Wpisz proponowaną ilość, by znów szła za regułą.">
              reguła proponuje {formatDecimal(line.ruleQuantity)}
            </span>
          )}
          {/* re-review 3: every line of a crew function carries its people now (the same list as the cost and „obsada: N”) */}
          {(line.origin === 'crew' || line.crewPeople) && (
            <span className="q-kind" title={line.crewPeople ? undefined : 'Pozycję dodała obsada wydarzenia: ilość to liczba osób w funkcji, dni to dni przydziału'} data-feature-id="quotes.crew_sync">
              {line.crewPeople ? <CrewPeopleInfo people={line.crewPeople} eventId={eventId} hovered={hovered} label={label} /> : 'z obsady'}
            </span>
          )}
          {line.crewQuantity != null && line.crewQuantity !== line.quantity && (
            <span
              className="q-kind warn"
              title={
                line.origin === 'crew' && !line.qtyOverridden && line.ruleQuantity != null && line.ruleQuantity === line.quantity
                  ? 'Obsada jest mniejsza niż przewiduje reguła: ilość zostaje na poziomie reguły, aż obsada ją przekroczy.'
                  : 'Pozycja zmieniona ręcznie: obsada jej nie nadpisze. „Uzupełnij z obsady” wyrówna ją do obsady.'
              }
            >
              obsada: {formatDecimal(line.crewQuantity)}
            </span>
          )}
          {line.origin === 'crew' && line.ruleQuantity != null && line.ruleQuantity > (line.crewQuantity ?? 0) && (
            <span className="q-kind" title="Reguła przewiduje tyle osób: to najmniejsza ilość, dopóki obsada jest mniejsza">
              min. wg reguły {formatDecimal(line.ruleQuantity)}
            </span>
          )}
          {line.transport && line.priceOverridden && (
            <span className="q-kind" title="Cena zmieniona w wierszu: zmiana odległości wydarzenia jej nie zmieni">
              cena wpisana ręcznie
            </span>
          )}
          {notice}
        </td>
        {cols.price && <td className="num q-price" data-label="Cena">{editable ? input('price', 'Cena') : text(formatPln(line.unitPriceGrosze ?? 0))}</td>}
        <td className="num q-narrow" data-label="Ilość">{editable ? input('quantity', 'Ilość') : text(formatDecimal(line.quantity))}</td>
        <td className="num q-narrow" data-label="Dni">{editable ? input('days', 'Ilość dni') : text(milliToDays(line.daysMilli))}</td>
        {cols.discount && !cols.discountHidden && <td className="num q-narrow" data-label="Rabat">{editable ? input('discount', 'Rabat %') : text(line.discountBp ? bpToPct(line.discountBp) : '')}</td>}
        {cols.net && (
          <td className="num q-net sheet-amount" aria-live={dirty ? 'polite' : undefined}>
            {text(shownNet === null ? '—' : formatPln(shownNet))}
          </td>
        )}
        {cols.cost && <CostCell line={line} editable={!!cols.costEditable} onSaved={onSaved} onConflict={onConflict} />}
        {cols.supplier && <SupplierCell line={line} editable={!!cols.supplierEditable} onSaved={onSaved} onConflict={onConflict} />}
        <td className="q-desc sheet-note">{editable ? input('description', 'Opis', false) : text(line.description)}</td>
        {(editable || select) && (
          <td className="q-actions">
            {ownGear && <RowMenu floating="end" label={`Więcej: ${label}`} featureId="packlists.extra_days" actions={[{ label: 'Dni rezerwacji…', onSelect: () => setEditingDays(true) }]} />}
            {/* owner 2026-10-24 16:15: the line's ⋮⋮ on the right, just before the × (where #161's section ⋮⋮ was) */}
            {select?.grip}
            {editable && (
              <button className="btn icon ghost q-remove" type="button" aria-label={`Usuń pozycję: ${label}`} title="Usuń pozycję" onClick={() => (onRemove ? onRemove(line) : void remove())}>
                ×
              </button>
            )}
            {editingDays && <ExtraDaysDialog name={label} value={line.extraDays} onSave={saveDays} onClose={() => setEditingDays(false)} />}
          </td>
        )}
      </tr>
      {error && (
        <tr className="sheet-row-error">
          <td colSpan={20} role="alert">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}
